import { Controller } from "@hotwired/stimulus"
import * as chrono from "chrono-node"

export default class extends Controller {
  static values = { 
    createUrl: String
  }
  
  // Fields that support paste
  static PASTE_FIELDS = ['title', 'suite', 'usage', 'area', 'lease_start_date', 'lease_end_date']
  
  connect() {
    this.boundHandlePaste = this.handlePaste.bind(this)
    this.element.addEventListener('paste', this.boundHandlePaste)
  }
  
  disconnect() {
    this.element.removeEventListener('paste', this.boundHandlePaste)
  }
  
  async handlePaste(event) {
    const target = event.target
    
    // Only handle paste on supported input fields
    if (!target.matches('input[name]')) return
    
    const fieldName = target.name
    if (!this.constructor.PASTE_FIELDS.includes(fieldName)) return
    
    const text = event.clipboardData.getData('text')
    
    // Only intercept multi-line paste
    if (!text.includes('\n')) return
    
    event.preventDefault()
    
    // Parse values
    const rawValues = text.split(/\r?\n/).filter(v => v.trim())
    if (rawValues.length === 0) return
    
    // Validate values based on field type
    const values = this.validateValues(rawValues, fieldName)
    if (values.length === 0) {
      console.warn('No valid values after validation')
      return
    }
    
    console.log('Paste values:', values)
    
    // Get current row and save its ID for later reference
    const currentRow = target.closest('tr')
    if (!currentRow) return
    
    const currentRowId = currentRow.id
    const tbody = currentRow.parentElement
    const allRows = Array.from(tbody.querySelectorAll('tr'))
    const startIndex = allRows.indexOf(currentRow)
    const rowsFromStart = allRows.slice(startIndex)
    
    // Calculate how many new tenants we need
    const neededNew = Math.max(0, values.length - rowsFromStart.length)
    
    // Create new tenants if needed
    if (neededNew > 0) {
      console.log(`Need to create ${neededNew} new tenants`)
      const success = await this.createTenantsWithTurbo(neededNew)
      if (!success) {
        console.error('Failed to create new tenants')
        return
      }
      
      // Wait for DOM to update after Turbo Stream replace
      await new Promise(r => setTimeout(r, 500))
    }
    
    // Get fresh tbody globally (table may have been replaced by Turbo Stream)
    const freshTbody = document.getElementById('tenants_table_body')
    if (!freshTbody) {
      console.error('Could not find tbody after update')
      return
    }
    
    console.log('Found tbody with', freshTbody.querySelectorAll('tr').length, 'rows')
    
    // Find the original row by ID and get rows from there
    const allUpdatedRows = Array.from(freshTbody.querySelectorAll('tr'))
    const originalRowIndex = allUpdatedRows.findIndex(row => row.id === currentRowId)
    
    console.log('Looking for row', currentRowId, 'found at index', originalRowIndex)
    
    let targetRows
    if (originalRowIndex >= 0) {
      // Start from original row
      targetRows = allUpdatedRows.slice(originalRowIndex)
    } else {
      // Fallback: fill from the beginning if we can't find original row
      console.log('Original row not found, filling from beginning')
      targetRows = allUpdatedRows.slice(0, values.length)
    }
    
    console.log('Target rows:', targetRows.length, 'values:', values.length)
    
    // Fill values into rows
    this.fillRows(targetRows, values, fieldName)
  }
  
  validateValues(values, fieldName) {
    return values.map(value => {
      const trimmed = value.trim()
      
      if (fieldName === 'area') {
        const num = parseFloat(trimmed.replace(',', '.'))
        if (isNaN(num) || num < 0) return null
        return num.toString()
      }
      
      if (fieldName === 'lease_start_date' || fieldName === 'lease_end_date') {
        // Try chrono-node parsing
        try {
          const parsed = chrono.parseDate(trimmed)
          if (parsed) {
            const day = String(parsed.getDate()).padStart(2, '0')
            const month = String(parsed.getMonth() + 1).padStart(2, '0')
            const year = parsed.getFullYear()
            return `${day}.${month}.${year}`
          }
        } catch (e) {
          console.warn('Chrono parse error:', e)
        }
        
        // Fallback: try common date formats manually
        const dateResult = this.parseDate(trimmed)
        if (dateResult) return dateResult
        
        return null
      }
      
      // For text fields (title, suite, usage)
      return trimmed
    }).filter(v => v !== null)
  }
  
  parseDate(str) {
    // Try DD.MM.YYYY or DD/MM/YYYY
    let match = str.match(/^(\d{1,2})[.\/](\d{1,2})[.\/](\d{4})$/)
    if (match) {
      const [, day, month, year] = match
      return `${day.padStart(2, '0')}.${month.padStart(2, '0')}.${year}`
    }
    
    // Try MM/DD/YYYY (US format)
    match = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
    if (match) {
      const [, month, day, year] = match
      return `${day.padStart(2, '0')}.${month.padStart(2, '0')}.${year}`
    }
    
    // Try YYYY-MM-DD (ISO)
    match = str.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (match) {
      const [, year, month, day] = match
      return `${day}.${month}.${year}`
    }
    
    return null
  }
  
  async createTenantsWithTurbo(count) {
    try {
      // Create tenants one by one using Turbo Stream to update DOM
      for (let i = 0; i < count; i++) {
        const response = await fetch(this.createUrlValue, {
          method: 'POST',
          headers: {
            'Accept': 'text/vnd.turbo-stream.html',
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
          }
        })
        
        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`)
        }
        
        // Let Turbo process the stream response
        const html = await response.text()
        
        // Turbo will automatically process turbo-stream responses
        // But since we're using fetch, we need to manually process it
        const template = document.createElement('template')
        template.innerHTML = html
        
        // Process each turbo-stream element
        template.content.querySelectorAll('turbo-stream').forEach(stream => {
          const action = stream.getAttribute('action')
          const target = stream.getAttribute('target')
          const targetElement = document.getElementById(target)
          
          if (action === 'replace' && targetElement) {
            const newContent = stream.querySelector('template').content.cloneNode(true)
            targetElement.replaceWith(newContent)
          } else if (action === 'append' && targetElement) {
            const newContent = stream.querySelector('template').content.cloneNode(true)
            targetElement.appendChild(newContent)
          }
        })
        
        // Small delay between creates to let DOM settle
        await new Promise(r => setTimeout(r, 50))
      }
      
      return true
    } catch (error) {
      console.error('Failed to create tenants:', error)
      return false
    }
  }
  
  fillRows(rows, values, fieldName) {
    console.log(`Filling ${values.length} values into ${rows.length} rows for field ${fieldName}`)
    values.forEach((value, index) => {
      if (index >= rows.length) {
        console.log(`Skipping value ${index}: no row available`)
        return
      }

      const row = rows[index]
      const input = row.querySelector(`input[name="${fieldName}"]`)

      if (input) {
        console.log(`Setting row ${index} input[name="${fieldName}"] to:`, value)
        input.value = value

        // Trigger input event to save the value via inline-tenant controller
        input.dispatchEvent(new Event('input', { bubbles: true }))
      } else {
        console.warn(`Row ${index}: input[name="${fieldName}"] not found`)
      }
    })
  }
}
